import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { useFilter } from '@/application/use-filter'
import { useCountries } from '@/application/queries'
import { toCountryCodes } from '@/domain/countries'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string,
  countries?: string[],
  collections?: string[]
) {
  return [
    'returnMetrics',
    ecommerceIds,
    startDate,
    endDate,
    countries,
    collections
  ]
}

export function useReturnMetrics(
  startDate: string | undefined,
  endDate: string | undefined
) {
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()
  const { getFilterValues: getSelectedCollections } = useFilter('collections')
  const selectedCollections = getSelectedCollections()

  function callReturnMetrics() {
    if (!ecommerceIds.length) return
    return AnalyticsApi.getReturnMetrics({
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate,
      countries: toCountryCodes(selectedCountries, countries),
      collections: selectedCollections
    })
  }

  const returnMetricsQuery = useQuery({
    queryKey: getQueryKey(
      ecommerceIds,
      startDate,
      endDate,
      selectedCountries,
      selectedCollections
    ),
    queryFn: callReturnMetrics,
    staleTime: 1000 * 60 * 15,
    enabled:
      ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries.length
  })

  return {
    returnMetrics: returnMetricsQuery.data,
    loadingReturnMetrics: returnMetricsQuery.isLoading || loadingCountries,
    returnMetricsFailed: returnMetricsQuery.isError
  }
}
